import type { ReactElement } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import styled from 'styled-components';

import { FavoriteFill } from '@rover/icons';
import Favorite from '@rover/icons/svg/favorite.svg';
import { Button } from '@rover/kibble/core';
import type { ButtonVariants } from '@rover/kibble/core/Button';
import type { HybridOnPressHandler } from '@rover/kibble/core/utils';
import useTheme from '@rover/kibble/themes/useTheme';
import type { RoverTheme } from '@rover/types/src/RoverTheme';
import type { SvgIcon } from '@rover/types/src/SvgIcon';

type FavoriteButtonVariant = 'flat' | 'default';

export interface Props {
  on?: boolean;
  onPress?: HybridOnPressHandler;
  variant?: FavoriteButtonVariant;
}

const getFavoriteButtonVariant = (theme: RoverTheme) => ({
  flat: {
    buttonVariant: (): ButtonVariants => 'flat',
    buttonStyles: (on: boolean): Record<string, unknown> =>
      on
        ? { color: `${theme.colors.text.success} !important` }
        : {
            color: theme.colors.text.primary,
            '&:hover': {
              color: theme.colors.text.primary,
            },
            '&:focus': {
              color: theme.colors.text.primary,
            },
          },
  },
  default: {
    buttonVariant: (on: boolean): ButtonVariants => (on ? 'primary' : 'secondary'),
    buttonStyles: (): Record<string, unknown> => ({
      color: theme.colors.text.primary,
      '&:hover': {
        color: theme.colors.text.primary,
      },
    }),
  },
});

const FavoriteFillHatch = styled(FavoriteFill)`
  @keyframes hatch {
    0% {
      transform: rotate(0deg) scaleY(0.6);
    }
    20% {
      transform: rotate(-2deg) scaleY(1.05);
    }
    35% {
      transform: rotate(2deg) scaleY(1);
    }
    50% {
      transform: rotate(-2deg);
    }
    65% {
      transform: rotate(1deg);
    }
    80% {
      transform: rotate(-1deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
  animation: hatch 2s ease-in-out;
  transform-origin: 50% 100%;
  visibility: visible !important;
`;

function usePrevious(value): boolean | undefined {
  const ref = useRef(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export default function FavoriteButton({
  on = false,
  variant = 'default',
  onPress,
  ...other
}: Props): ReactElement {
  const { t } = useLingui();
  const [favoriting, setFavoriting] = useState(false);
  const prevFavoriting = usePrevious(favoriting);
  const theme = useTheme();

  const favoriteButtonVariant = getFavoriteButtonVariant(theme)[variant];

  const getFavoriteIcon = (): SvgIcon => {
    if (on) {
      if (favoriting && !prevFavoriting) {
        return FavoriteFillHatch;
      }
      return FavoriteFill;
    }
    return Favorite;
  };
  return (
    <Button
      {...other}
      onPress={({ webEvent }) => {
        setFavoriting(!on);
        onPress?.({ webEvent });
      }}
      webProps={{ 'aria-label': t`Add this sitter to my favorites` }}
      aria-pressed={on}
      icon={getFavoriteIcon()}
      size="small"
      variant={favoriteButtonVariant.buttonVariant(on)}
      sx={{
        p: '2x',
        ...favoriteButtonVariant.buttonStyles(on),
      }}
    />
  );
}
